(ns app.front.view.inspections.booking-utils
  (:require [app.domain.blackout.model :as blackout-model]
            [app.domain.inspection.model :as inspection-model]
            [clojure.string :as str]
            [core.front.date-util :as date-util]
            [potpuri.core :as p]))

(defn single-time-data [{:keys [date time-slot-id]}]
  (when (and date time-slot-id)
    {:time-slot-id time-slot-id
     :year         (-> date .getFullYear)
     :month        (-> date .getMonth inc)
     :day          (-> date .getDate)}))


(defn payload-time-data [date-time-slots]
  {:dates (keep single-time-data date-time-slots)})


(defn time-slot-string [slot group-tz]
  (let [create-date (fn [hour min]
                      (-> (doto (js/Date.)
                            (.setHours hour)
                            (.setMinutes min))
                          (date-util/in-tz group-tz)))
        start-time  (create-date (:time-slot/start-hour slot) (:time-slot/start-minute slot))
        end-time    (create-date (:time-slot/end-hour slot) (:time-slot/end-minute slot))]
    (str (date-util/hour-minute-in-tz start-time group-tz)
         " – "
         (date-util/hour-minute-in-tz end-time group-tz))))


(defn time-slots-for-weekday
  "Returns all time slots for a given weekday.
   `js-weekday` is a number from 0 (Sunday) to 6 (Saturday)."
  [js-weekday time-slots]
  (->> time-slots
       (filter #(= (:time-slot/weekday %) js-weekday))))


(defn valid-availability [date availabilities]
  (or (->> availabilities
           (filter #(-> %
                        :entity/valid-from
                        date-util/start-of-day
                        (date-util/on-or-before? date)))
           (sort-by :entity/valid-from)
           last)
      (first availabilities)))


(defn allow-date?
  ([group-tz time-slots blackouts date]
   (let [weekday (some-> date .getDay)
         date    (date-util/in-tz date group-tz)]
     (and (not-any? (partial blackout-model/applies-to-date? date) blackouts) ; `date` not blacked out?
          (boolean (seq (time-slots-for-weekday weekday time-slots)))))) ; `date` has time slots available?

  ([min-date group-tz time-slots blackouts date]
   (and (date-util/on-or-after? date min-date)
        (allow-date? group-tz time-slots blackouts date))))


(defn allow-date-fn
  ([group-tz time-slots blackouts] (fn [date] (allow-date? group-tz time-slots blackouts date)))
  ([min-date group-tz time-slots blackouts] (fn [date] (allow-date? min-date group-tz time-slots blackouts date))))


(defn create-booking-command [{:keys [target-id inspection-id date-time-slots inspector-id notes on-success]}]
  (let [payload (p/assoc-if (payload-time-data date-time-slots)
                            :inspection-id inspection-id
                            :inspector-id inspector-id
                            :notes notes)]
    {:comm/command {:command    :process/apply-signal
                    :data       {:process/signal [:process.signal/create-booking payload]
                                 :entity-id      target-id}
                    :on-success on-success}}))


(defn inspection-result-or-booking-to-string
  "Returns a string based on the last visit/booking"
  [tr lang active-booking {:inspection.visit/keys [result visit-result result-details failure-reason in-progress?]}]
  (cond
    in-progress?
    (tr [:inspection.visit/visit-ongoing])

    active-booking
    (tr [(:domain/state active-booking)])

    (some? visit-result)
    (str (inspection-model/visit-result-name visit-result lang)
         (when (seq result-details)
           (str ": " (str/join ", " (map #(inspection-model/visit-result-detail-name % lang) result-details)))))

    (some? result) ; remove when :feature-flag.id/inspection-visit-configuration removed
    (cond-> (tr [result])
      (some? failure-reason) (str ": " (tr [failure-reason])))

    :else
    (tr [:booking/not-requested])))


(defn inspection-with-last-visit-status
  "Goes through inspection's visits and returns the last visit result and failure reason with the inspection."
  [inspection]
  (let [last-visit         (->> inspection
                                inspection-model/inspection-visits
                                (filter :inspection.visit/start-time)
                                (sort-by :inspection.visit/start-time >)
                                first)
        visit-in-progress? (= :inspection.visit.state/in-progress (:domain/state last-visit))]
    {:inspection inspection
     :visit      (merge {:inspection.visit/in-progress? visit-in-progress?}
                        (select-keys last-visit [:inspection.visit/result :inspection.visit/failure-reason :inspection.visit/visit-result :inspection.visit/result-details]))}))
